<template>
  <div v-if="loading" class="page-loading">
    <Loading />
  </div>
  <div v-if="!isDeveloperInfoVisible">
    <div>
      <div>
        <div class="strip fs-14 between-flex">
          <div class="w-25">
            <SearchAndFilter
              v-model="searchQuery"
              :nullable="true"
              :showInput="true"
              :showSelect="false"
              :placeholder="$t('DeveloperLists.searchByDeveloperName')"
              @submitForm="handleSearchFilterChange"
            />
          </div>
          <div class="filter-container">
            <FilterComponent
              :sortCriteria="sortListCriteria"
              :sortOrder="sortListOrder"
              :sortingOptions="sortingListOptions"
              :isOrderOptions="false"
              :dafaultNoneOption="$t('List.name')"
              @filter-change="handleListFilterChange"
            />
            <FilterComponent
              :sortCriteria="sortCriteria"
              :sortOrder="sortOrder"
              :sortingOptions="sortingOptions"
              :orderOptions="orderOptions"
              :isOrderOptions="true"
              @filter-change="handleFilterChange"
            />
            <MultiSelectFilterComponent
              :available-filters="platformFilters"
              :initial-selected="selectedFilters"
              :placeholder-text="$t('Custom.allPlatforms')"
              @update:selected-filters="handlePlatformFilterChange"
            />
          </div>
        </div>
        <NotFoundContent
          v-if="filteredAndSortedItems.length === 0"
          message="Not found result."
        />
        <div v-else class="list-items-content">
          <TableComponent>
            <template v-slot:table-header>
              <tr>
                <th v-for="header in tableHeaders" :key="header.key">
                  {{ header.label }}
                </th>
              </tr>
            </template>
            <template v-slot:table-body>
              <tr
                v-for="(data, index) in filteredAndSortedItems"
                :key="data.item.id"
                :class="{ 'zebra-stripe': index % 2 === 0 }"
              >
                <td>{{ index + 1 }}</td>
                <td>
                  <div class="developer-icon" v-if="data.item.icon">
                    <img :src="data.item.icon" :alt="name" />
                  </div>
                  <div v-else class="developer-icon no-icon">
                    <span class="material-symbols-outlined"> code_blocks </span>
                  </div>
                </td>
                <td>
                  <a :href="data.item.link" target="_blank">
                    <img
                      :src="getStoreImage(data.item.platformTypeId)"
                      alt="Platform Icon"
                      class="platform-icon"
                    />
                  </a>
                </td>
                <td>
                  <a
                    class="clickable-text"
                    :href="
                      $router.resolve({
                        name: 'Developer',
                        params: { storeId: data.item.storeId },
                      }).href
                    "
                    @click.stop="
                      handleLinkClick($event, {
                        name: 'Developer',
                        params: { storeId: data.item.storeId },
                      })
                    "
                    v-html="data.item.name || 'N/A'"
                    ></a
                  >
                </td>
                <td>{{ data.item?.appCount || "" }}</td>
                <td>{{ formatDate(data.created_at) || "" }}</td>
                <td>
                  <div class="action-buttons">
                    <div
                      class="action-button main"
                      @click.stop="handleAddToListModal(data)"
                    >
                      <span class="material-symbols-outlined notification-icon">
                        list_alt_add
                      </span>
                      <span>{{ $t("List.addToList") }}</span>
                    </div>
                    <div
                      class="action-button"
                      :class="{
                        green: data?.isNotification,
                        red: !data?.isNotification,
                      }"
                      style="min-width: 84px"
                      @click.stop="
                        handleNotification(
                          data.item.id,
                          data.isNotification,
                          index
                        )
                      "
                    >
                      <template v-if="loadingIndex === index">
                        <span class="button-spinner"></span>
                      </template>
                      <template v-else>
                        <span
                          class="material-symbols-outlined notification-icon"
                        >
                          {{
                            data.isNotification
                              ? "notifications_active"
                              : "notification_add"
                          }}
                        </span>
                        <span>{{
                          data.isNotification ? "Is On" : "Is Off"
                        }}</span>
                      </template>
                    </div>
                    <div
                      class="action-button"
                      @click.stop="handleAreYouSureModal(data)"
                    >
                      <span class="material-symbols-outlined remove-icon"
                        >delete_sweep</span
                      >
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </TableComponent>
          <div class="pagination-wrapper">
            <PaginationComponent
              :meta="pagination"
              @page-changed="fetchLibraryItems"
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  <DeveloperInfo
    v-if="isDeveloperInfoVisible"
    :developerData="developerData"
    :isBack="true"
    @close="handleBack"
  />
  <AreYouSureModal
    v-if="showModal"
    :isVisible="showModal"
    :title="modalTitle"
    :message="modalMessage"
    :itemId="selectedAppId"
    :confirmButtonLabel="$t('Custom.remove')"
    :cancelButtonLabel="$t('Custom.cancel')"
    @result="handleModalResult"
  />
  <NotificationComponent
    v-if="notification.show"
    :key="notification.key"
    :type="notification.type"
    :title="notification.title"
    :message="notification.message"
    @closed="!notification.show"
  />

  <Modal
    :isOpen="ShowAddToListModal"
    :modalTitle="modalTitle"
    :actionButton="developerList.length"
    actionType="Add To List"
    @close="closeModal"
    @action="handleAddToListAction"
  >
    <NotFoundContent
      v-if="developerList.length === 0"
      message="Not Found List"
    />
    <div v-else class="list-container">
      <label
        v-for="item in sortedDeveloperList"
        :key="item.id"
        class="list-item"
      >
        <input
          type="checkbox"
          :value="item.id"
          v-model="newSelectedLists"
          :disabled="isAlreadySaved(item.id)"
          @change="clearWarning"
        />
        {{ item.name }}
        <span v-if="isAlreadySaved(item.id)" class="saved-tag">
          (Already Added)
        </span>
      </label>
    </div>

    <p v-if="showWarning" class="warning-message">
      Please select at least one list.
    </p>
  </Modal>
</template>

<script>
import Loading from "@/components/user/layout/LoadingComponent.vue";
import NotFoundContent from "@/components/user/common/NotFoundContent.vue";
import ItemCard from "@/components/user/common/ItemCard.vue";
import SearchAndFilter from "@/components/user/common/SearchAndFilter.vue";
import Modal from "@/components/custom/modals/ModalComponent.vue";
import AreYouSureModal from "@/components/custom/modals/AreYouSureModal.vue";
import NotificationComponent from "@/components/custom/notifications/NotificationComponent.vue";
import DeveloperInfo from "@/components/user/viewComponent/developer/DeveloperInfo.vue";
import PlatformTypeEnum from "@/enums/PlatformTypeEnum";
import MultiSelectFilterComponent from "@/components/user/common/MultiSelectFilterComponent.vue";
import FilterComponent from "@/components/user/common/FilterComponent.vue";
import {
  getDeveloperLibrary,
  removeDeveloperFromAll,
  updateNotification,
} from "@/api/developerLibrary.api";
import { getApp } from "@/api/app.api";
import TableComponent from "@/components/custom/table/TableComponent.vue";
import { storeImage } from "@/ultis/user/functions/storePlatform";
import ListTypeEnum from "@/enums/ListTypeEnum";
import { getListByListType } from "@/api/list.api";
import { AddDeveloperToMultipleLists } from "@/api/developerList.api";
import PaginationComponent from "@/components/user/common/PaginationComponent.vue";

export default {
  name: "LibraryComponent",
  components: {
    Loading,
    NotFoundContent,
    ItemCard,
    SearchAndFilter,
    AreYouSureModal,
    DeveloperInfo,
    NotificationComponent,
    MultiSelectFilterComponent,
    TableComponent,
    FilterComponent,
    Modal,
    PaginationComponent,
  },
  data() {
    return {
      notification: {
        show: false,
        key: 0,
        type: "",
        title: "",
        message: "",
      },
      pagination: {
        current_page: 1,
        last_page: 1,
        next_page_url: null,
        prev_page_url: null,
      },
      libraryItems: [],
      listByListType: [],
      developerMenuId: null,
      loading: true,
      isDeveloperInfoVisible: false,
      developerData: null,
      url: null,
      showModal: false,
      selectedDeveloperId: null,
      sortCriteria: "created_at",
      sortOrder: "desc",
      searchQuery: "",
      sortingOptions: [
        { value: "name", label: this.$t("Filter.name") },
        { value: "appCount", label: this.$t("Filter.appCount") },
        { value: "created_at", label: this.$t("Filter.addedAt") },
      ],
      orderOptions: [
        { value: "asc", label: this.$t("Filter.ascending") },
        { value: "desc", label: this.$t("Filter.descending") },
      ],
      showFilterDropdown: false,
      selectedFilters: [],
      platformFilters: PlatformTypeEnum.getAllOptions(),
      tableHeaders: [
        { key: "index", label: this.$t("Table.index") },
        { key: "icon", label: this.$t("Table.icon") },
        { key: "platform", label: this.$t("Table.platform") },
        { key: "name", label: this.$t("Table.name") },
        { key: "appCount", label: this.$t("Table.appCount") },
        { key: "addedAt", label: this.$t("Table.addedAt") },
      ],
      isNotificationLoading: false,

      developerList: [],
      developerSavedList: [],
      selectedLists: [],
      newSelectedLists: [],
      selectedDeveloper: null,
      showWarning: false,
      ShowAddToListModal: false,
      sortListCriteria: null,
      sortListOrder: "",
      searchListQuery: "",
      sortingListOptions: [],
      loadingIndex: null,
    };
  },
  computed: {
    filteredAndSortedItems() {
      if (!this.libraryItems) return [];
      let items = [...this.libraryItems];
      return items;
      
      if (this.searchQuery) {
        items = items.filter((item) =>
          item.item.name.toLowerCase().includes(this.searchQuery.toLowerCase())
        );
      }

      if (this.selectedFilters.length > 0) {
        items = items.filter((item) =>
          this.selectedFilters.includes(item.item.platformTypeId)
        );
      }

      if (this.sortListCriteria !== "All") {
        items = items.filter((item) => {
          return item.list.some(
            (list) => list.id.toString() === this.sortListCriteria
          );
        });
      }

      return items.sort((a, b) => {
        const modifier = this.sortOrder === "desc" ? 1 : -1;
        switch (this.sortCriteria) {
          case "name":
            return a.item.name.localeCompare(b.item.name) * modifier;
          case "appCount":
            return (a.item.appCount - b.item.appCount) * modifier;
          case "created_at":
            const createdAtA = new Date(a.item.created_at).getTime();
            const createdAtb = new Date(b.item.created_at).getTime();
            if (isNaN(createdAtA) || isNaN(createdAtb)) return 0;
            return (createdAtA - createdAtb) * modifier;
          default:
            return 0;
        }
      });
    },
    sortedDeveloperList() {
      return [...this.developerList].sort((a, b) => {
        const aSaved = this.isAlreadySaved(a.id);
        const bSaved = this.isAlreadySaved(b.id);
        if (aSaved !== bSaved) return aSaved - bSaved;
        return a.name.localeCompare(b.name);
      });
    },
  },
  async created() {
    Promise.all([
      await this.getListByListType(),
      await this.fetchLibraryItems(),
    ]);
    this.sortingListOptions = this.listByListType.map((list) => ({
      value: list.id.toString(),
      label: list.name,
    }));
    this.sortingListOptions.unshift({ value: null, label: "All List" });
  },
  methods: {
    async handleAddToListAction() {
      if (this.newSelectedLists.length === 0) {
        this.showWarning = true;
        return;
      }
      this.loading = true;
      await this.addDeveloperToLists(
        this.selectedDeveloper,
        this.newSelectedLists
      );
      this.loading = false;
      this.closeModal();
    },
    async handleAddToListModal(item) {
      this.loading = true;
      this.selectedDeveloper = item.itemId;
      this.developerList = this.listByListType.filter(
        (data) =>
          data.platformTypeId === item.item.platformType.id ||
          data.platformTypeId === PlatformTypeEnum.ALL
      );
      this.developerSavedList = item.list;
      this.selectedLists = this.developerSavedList.map((saved) => saved.listId);
      this.modalTitle = "Add To List" + " - " + item.item.name;
      this.ShowAddToListModal = true;
      this.loading = false;
    },
    async getListByListType() {
      try {
        const response = await getListByListType(ListTypeEnum.DEVELOPER);
        if (response.data.status) {
          this.listByListType = response.data.data;
        }
      } catch (error) {}
    },
    async addDeveloperToLists(developerId, listIds) {
      try {
        const response = await AddDeveloperToMultipleLists(
          developerId,
          listIds
        );
        if (response.data.status) {
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
          await this.fetchLibraryItems();
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      }
    },
    closeModal() {
      this.selectedLists = [];
      this.newSelectedLists = [];
      this.developerList = [];
      this.developerSavedList = [];
      this.selectedDeveloper = null;
      this.ShowAddToListModal = false;
    },
    clearWarning() {
      this.showWarning = false;
    },
    isAlreadySaved(listId) {
      return this.developerSavedList.some((saved) => saved.id === listId);
    },
    async handleListFilterChange({ criteria }) {
      this.sortListCriteria = criteria;
      await this.fetchLibraryItems();
    },
    handleLinkClick(event, routeData) {
      const url = this.$router.resolve(routeData).href;
      if (event.ctrlKey || event.metaKey || event.button === 2) {
        window.open(url, "_blank");
        return;
      }
      this.$router.push(routeData);
    },
    openLink(link) {
      if (link) {
        window.open(link, "_blank", "noopener,noreferrer");
      }
    },
    getStoreImage(platformTypeId) {
      return storeImage(platformTypeId);
    },
    async handlePlatformFilterChange(filters) {
      this.selectedFilters = filters;
      await this.fetchLibraryItems();
    },
    async handleFilterChange({ criteria, order }) {
      this.sortCriteria = criteria;
      this.sortOrder = order;
      await this.fetchLibraryItems();
    },
    handleBack() {
      this.isDeveloperInfoVisible = false;
      this.developerData = null;
    },
    formatDate(date) {
      if (!date) return "";
      const locale = localStorage.getItem("localeTime") || "en-US";
      return new Date(date).toLocaleDateString(locale);
    },
    async handleSearchFilterChange() {
      await this.fetchLibraryItems();
    },
    async fetchLibraryItems(page = 1,) {
      this.loading = true;
      try {
        const response = await getDeveloperLibrary(page, this.sortCriteria, this.sortOrder, this.selectedFilters, this.sortListCriteria, this.searchQuery);
        if (response.data.status) {
          this.libraryItems = response.data.data.data;
          this.pagination = response.data.data.meta;
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            this.$t("Validation.validEmail")
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loading = false;
      }
    },
    async handleShow(storeId) {
      this.$router.push({
        name: "Developer",
        params: { storeId: storeId },
      });
    },
    async handleNotification(developerId, isNotification, index) {
      this.loadingIndex = index;
      try {
        const response = await updateNotification(developerId, !isNotification);
        if (response.data.status) {
          this.fetchLibraryItems();
          this.showNotification("success", "Suceess", response.data.msg);
        } else {
          this.showNotification("error", "Error", response.data.msg);
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loadingIndex = null;
      }
    },
    async fetchData(url = this.url) {
      this.loading = true;
      try {
        await this.processDeveloperLink(url);
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loading = false;
      }
    },
    async processDeveloperLink(url = this.url) {
      if (!url) return;

      try {
        const response = await getApp(url);

        if (response.data.status) {
          this.developerData = response.data.data;
        } else {
          this.showNotification("success", "Suceess", response.data.msg);
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
      }
    },
    async removeDeveloper(developerId) {
      this.loading = true;
      try {
        const response = await removeDeveloperFromAll(developerId);
        if (response.data.status) {
          await this.fetchLibraryItems();
          this.showNotification("success", "Suceess", response.data.msg);
        } else {
          this.showNotification("error", "Error", response.data.msg);
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      }
    },
    handleAreYouSureModal(data) {
      this.modalTitle = this.$t("Custom.areYouSure");
      this.modalMessage =
        this.$t("Custom.doYouWantRemoveThis") +
        "<b>" +
        data.item.name +
        "</b> ? " +
        this.$t("Custom.thisActionCannotBeUndone") +
        "<p style='color: red'>" +
        this.$t("Custom.deleteFromAllLists") +
        "</p> ";
      this.selectedDeveloperId = data.itemId;
      this.showModal = true;
    },
    setOpenItemMenu(menuId) {
      this.developerMenuId = this.developerMenuId === menuId ? null : menuId;
    },
    generateButtons(item) {
      return [
        {
          label: this.$t("Custom.show"),
          icon: "visibility",
          disable: false,
          action: () => this.handleShow(item.itemId),
        },
        {
          label: this.$t("Custom.remove"),
          icon: "remove",
          disable: false,
          action: () => {
            this.handleAreYouSureModal(item), this.setOpenItemMenu(item.itemId);
          },
        },
      ];
    },
    handleModalResult(result) {
      this.showModal = false;
      if (result) {
        this.removeDeveloper(this.selectedDeveloperId);
      }
    },
    showNotification(type, title, message) {
      this.notification = {
        show: true,
        key: Date.now(),
        type,
        title,
        message,
      };
    },
    toggleFilterDropdown() {
      this.showFilterDropdown = !this.showFilterDropdown;
    },
    handleClickOutside(event) {
      if (
        this.$refs.filterDropdown &&
        !this.$refs.filterDropdown.contains(event.target)
      ) {
        this.showFilterDropdown = false;
      }
    },
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
};
</script>

<style scoped>
/** List Modal */
.add-to-list-modal {
  padding: 15px;
}

.list-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 250px;
  overflow-y: auto;
}

.list-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  font-size: 14px;
  cursor: pointer;
}

.list-item input {
  cursor: pointer;
  transform: scale(1.2);
}

.list-item input:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.saved-tag {
  color: gray;
  font-size: 0.8rem;
}

.warning-message {
  color: red;
  font-size: 0.8rem;
  margin-top: 10px;
  text-align: center;
}

/** */
.filter-container {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  align-items: center;
}

.strip {
  overflow-x: visible !important;
}

.developer-icon {
  width: 40px;
  height: 40px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.developer-icon span {
  font-size: 16px;
}

.developer-icon.no-icon {
  background: #fff6ee;
  color: var(--logo-color);
}

.developer-icon img {
  width: 100%;
  height: 100%;
  border-radius: 16px;
  object-fit: cover;
}

@media (max-width: 640px) {
  .developer-icon {
    width: 35px;
    height: 35px;
  }
}
</style>
